// src/pages/admin/AdminSupportMail.jsx
// Back-office : lecture de la boîte support@artkiv.com via l'API Hostinger Mail.
// V1 (brief « Intégration Hostinger Mail API ») : prouver que la connexion fonctionne, puis
// lister les derniers messages. Lecture seule. Le token Hostinger reste dans les secrets
// Supabase : cette page n'appelle que la fonction `support-mail` (réservée aux admins).
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

const ERRORS = {
  hostinger_not_configured: "Le token Hostinger n'est pas dans les secrets Supabase (HOSTINGER_MAIL_API_TOKEN).",
  hostinger_auth_failed: "Hostinger refuse le token (invalide, expiré ou sans droit de lecture).",
  mailbox_not_found: "Le token fonctionne, mais la boîte support@artkiv.com n'en fait pas partie.",
  hostinger_list_failed: "Hostinger n'a pas pu lister les messages.",
  forbidden: "Réservé aux administrateurs.",
  unauthorized: "Session expirée : reconnectez-vous.",
};

async function callSupportMail(body) {
  const { data, error } = await supabase.functions.invoke("support-mail", { body });
  if (!error) return { ok: true, data };
  let payload = null;
  try { payload = await error.context?.json?.(); } catch { /* corps illisible */ }
  return { ok: false, code: payload?.error || "unknown", detail: payload?.detail, extra: payload };
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

export default function AdminSupportMail() {
  const [conn, setConn] = useState({ state: "loading" });
  const [mails, setMails] = useState({ state: "idle", rows: [], page: 1, pagination: null });

  const test = useCallback(async () => {
    setConn({ state: "loading" });
    const r = await callSupportMail({ action: "test" });
    setConn(r.ok ? { state: "ok", ...r.data } : { state: "error", code: r.code, detail: r.detail, extra: r.extra });
  }, []);

  const load = useCallback(async (page = 1) => {
    setMails((m) => ({ ...m, state: "loading" }));
    const r = await callSupportMail({ action: "list", folder: "INBOX", page, perPage: 25 });
    if (r.ok) setMails({ state: "ok", rows: r.data.emails || [], page, pagination: r.data.pagination });
    else setMails({ state: "error", rows: [], page, pagination: null, code: r.code, detail: r.detail });
  }, []);

  useEffect(() => { test(); }, [test]);

  const msg = (code, detail) => ERRORS[code] || `Erreur (${code}).`;

  return (
    <div className="p-6 max-w-5xl mx-auto" style={{ fontFamily: "Georgia, serif" }}>
      <h1 className="text-2xl mb-1">Boîte support</h1>
      <p className="text-sm text-gray-500 mb-6">Lecture seule de support@artkiv.com via Hostinger Mail. Le token n'est jamais exposé au navigateur.</p>

      {/* Étape 1 : connexion */}
      <div className="border rounded-lg p-4 mb-6 bg-white">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Connexion Hostinger</div>
            {conn.state === "loading" && <div className="text-sm">Test en cours…</div>}
            {conn.state === "ok" && (
              <div className="text-sm text-green-800">
                ✓ Connecté — boîte <strong>{conn.address}</strong> reconnue.
              </div>
            )}
            {conn.state === "error" && (
              <div className="text-sm text-red-700">
                ✗ {msg(conn.code)}
                {conn.extra?.mailboxesFound?.length ? (
                  <div className="text-xs text-gray-600 mt-1">Boîtes accessibles avec ce token : {conn.extra.mailboxesFound.join(", ")}</div>
                ) : null}
              </div>
            )}
          </div>
          <button onClick={test} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50">Retester</button>
        </div>
      </div>

      {/* Étape 2 : messages */}
      <div className="border rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="text-xs uppercase tracking-widest text-gray-500">Derniers messages (boîte de réception)</div>
          <button
            onClick={() => load(1)}
            disabled={conn.state !== "ok" || mails.state === "loading"}
            className="px-3 py-1.5 bg-black text-white rounded text-sm disabled:opacity-40"
          >
            {mails.state === "loading" ? "Chargement…" : "Charger les messages"}
          </button>
        </div>

        {mails.state === "error" && <p className="text-sm text-red-700">✗ {msg(mails.code)}</p>}
        {mails.state === "ok" && mails.rows.length === 0 && <p className="text-sm text-gray-500">Aucun message.</p>}

        {mails.rows.length > 0 && (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b text-xs text-gray-500">
                  <th className="py-2 pr-3">Date</th><th className="pr-3">De</th><th className="pr-3">Objet</th><th></th>
                </tr>
              </thead>
              <tbody>
                {mails.rows.map((m) => (
                  <tr key={m.id} className={`border-b align-top ${m.unseen ? "font-semibold" : ""}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(m.date)}</td>
                    <td className="pr-3">{m.from ? (m.from.name ? `${m.from.name} ` : "") : "—"}{m.from && <span className="text-gray-500 font-normal">&lt;{m.from.email}&gt;</span>}</td>
                    <td className="pr-3">{m.subject || "(sans objet)"}</td>
                    <td className="text-right whitespace-nowrap">{m.hasAttachments ? "📎" : ""}{m.unseen ? " ●" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between mt-3 text-sm">
              <button onClick={() => load(mails.page - 1)} disabled={mails.page <= 1 || mails.state === "loading"} className="px-3 py-1 border rounded disabled:opacity-40">← Plus récents</button>
              <span className="text-gray-500">Page {mails.page}</span>
              <button onClick={() => load(mails.page + 1)} disabled={mails.rows.length < 25 || mails.state === "loading"} className="px-3 py-1 border rounded disabled:opacity-40">Plus anciens →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
